import { MaturityLevel } from "../config/types";
import { FeatureFlagNames } from "../constants/FeatureFlags";
import debugLogger from "../utils/debugLogger";

export interface FeatureFlag {
  name: string;
  enabled: boolean;
  description: string;
  module?: string;
  rolloutPercentage?: number;
  maturity?: MaturityLevel;
}

export class FeatureFlags {
  private flags: Map<string, FeatureFlag> = new Map();
  private userRolloutMap: Map<string, Set<string>> = new Map();
  private platformConfigInitialized: boolean = false;

  constructor() {
    this.initializeDefaultFlags();
  }

  /**
   * Initialize feature flags from platform configuration
   * This should be called after platform config is loaded
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public initializeFromPlatformConfig(platformConfig: any): void {
    if (this.platformConfigInitialized) {
      return;
    }

    if (
      !platformConfig ||
      !platformConfig.platform ||
      !platformConfig.platform.featureFlags
    ) {
      debugLogger.warn("Platform config does not contain feature flags.");
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    platformConfig.platform.featureFlags.forEach((flagConfig: any) => {
      const flag: FeatureFlag = {
        name: flagConfig.name,
        enabled: flagConfig.defaultEnabled || false,
        description: flagConfig.description || "",
        maturity: flagConfig.maturity as MaturityLevel,
        rolloutPercentage: flagConfig.defaultEnabled ? 100 : 0,
      };
      this.flags.set(flag.name, flag);
    });

    this.platformConfigInitialized = true;
    debugLogger.info("FeatureFlags initialized from platform config.");
  }

  private initializeDefaultFlags(): void {
    const defaultFlags: FeatureFlag[] = [
      {
        name: FeatureFlagNames.NEW_AUTH_MODULE,
        enabled: false,
        description: "Enable new event-driven auth module",
        module: "auth",
        rolloutPercentage: 0,
      },
    ];
    defaultFlags.forEach((flag) => this.flags.set(flag.name, flag));
  }

  enable(flagName: string, rolloutPercentage: number = 100): void {
    const flag = this.flags.get(flagName);
    if (flag) {
      flag.enabled = true;
      flag.rolloutPercentage = rolloutPercentage;
    }
  }

  disable(flagName: string): void {
    const flag = this.flags.get(flagName);
    if (flag) {
      flag.enabled = false;
      flag.rolloutPercentage = 0;
    }
  }

  isEnabled(flagName: string, userId?: string): boolean {
    const flag = this.flags.get(flagName);
    if (!flag || !flag.enabled) return false;
    if (flag.rolloutPercentage === 100) return true;
    if (!userId || flag.rolloutPercentage === 0) return false;
    const userRollouts = this.userRolloutMap.get(userId) || new Set();
    if (userRollouts.has(flagName)) return true;
    const userHash = this.hashUserId(userId);
    const shouldRollout = userHash % 100 < (flag.rolloutPercentage ?? 0);
    if (shouldRollout) {
      userRollouts.add(flagName);
      this.userRolloutMap.set(userId, userRollouts);
    }
    return shouldRollout;
  }

  private hashUserId(userId: string): number {
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  }

  getFlagsForModule(moduleName: string): FeatureFlag[] {
    return Array.from(this.flags.values()).filter(
      (flag) => flag.module === moduleName,
    );
  }

  getAllFlags(): FeatureFlag[] {
    return Array.from(this.flags.values());
  }

  /**
   * Get flags by maturity level
   */
  getFlagsByMaturity(maturity: MaturityLevel): FeatureFlag[] {
    return Array.from(this.flags.values()).filter(
      (flag) => flag.maturity === maturity,
    );
  }

  /**
   * Check if component should be enabled based on its feature flags
   * This is a helper method for component-level flag checking
   */
  areComponentFlagsEnabled(flagNames: string[], userId?: string): boolean {
    if (flagNames.length === 0) return true;
    return flagNames.every((flagName) => this.isEnabled(flagName, userId));
  }

  /**
   * Get flag metadata
   */
  getFlag(name: string): FeatureFlag | undefined {
    return this.flags.get(name);
  }

  /**
   * Check if platform config has been initialized
   */
  isPlatformConfigInitialized(): boolean {
    return this.platformConfigInitialized;
  }
}

export const featureFlags = new FeatureFlags();
